import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleLoginClick = () => navigate('/login');

  const menuItems = [
    { label: 'Job Seekers', href: '/job-seeker' },
    { label: 'Career Centers', href: '/career-centers' },
    { label: 'Employers', href: '/employer' },
    {
      label: 'Company',
      dropdown: [
        { label: 'About Us', href: '/about-us' },
        { label: 'Features', href: '/features' },
      ],
    },
  ];

  const toggleDropdown = (label) => {
    setActiveDropdown((prev) => (prev === label ? null : label));
  };

  return (
    <header className="tw-fixed tw-w-full tw-top-0 tw-z-[1001] tw-bg-header-1 tw-shadow-[0px_4px_16px_#00000014] tw-px-4 sm:tw-px-6 lg:tw-px-14">

      <div className="tw-flex tw-justify-between tw-items-center tw-py-4 lg:tw-py-0 lg:tw-h-16">
        <a href="/" className="tw-flex tw-items-center">
          <img src="images/logo/Logo1.png" alt="logo" className="tw-h-[2.5rem] tw-w-auto" />
        </a>

        <button
          className="tw-block lg:tw-hidden tw-p-2 tw-z-[200] tw-bg-transparent tw-border-none tw-outline-none focus:tw-outline-none"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="tw-w-6 tw-h-6 tw-text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="tw-w-6 tw-h-6 tw-text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Desktop Menu */}
        <div className="tw-hidden lg:tw-flex tw-items-center tw-justify-between tw-flex-1 tw-ml-8">
          <nav className="tw-flex tw-items-center tw-gap-8">
            {menuItems.map((item, index) => {
              const isDropdownActive = item.dropdown?.some(sub => sub.href === currentPath);

              if (item.dropdown) {
                return (
                  <div key={index} className="tw-relative tw-group tw-inline-block">
                    <span className={`tw-font-semibold tw-text-base tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-2 tw-transition ${isDropdownActive ? 'tw-active-nav-link-dark tw-shine-effect' : 'tw-text-global-6 hover:tw-text-blue-3 hover:tw-bg-white/10 hover:tw-backdrop-blur-md hover:tw-rounded-lg hover:tw-shadow-md'}`}>
                      {item.label}
                      <svg className="tw-w-4 tw-h-4 tw-transition-transform group-hover:tw-rotate-180" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                    <div className="tw-absolute tw-left-0 tw-top-full tw-z-[105] tw-opacity-0 tw-invisible group-hover:tw-opacity-100 group-hover:tw-visible tw-transition-opacity tw-duration-200 tw-bg-white tw-shadow-lg tw-mt-1 tw-rounded-md tw-min-w-[160px] tw-border tw-border-gray-200">
                      {item.dropdown.map((subItem, subIndex) => (
                        <NavLink key={subIndex} to={subItem.href} className="tw-block tw-whitespace-nowrap tw-text-black tw-font-normal tw-text-sm hover:tw-bg-gray-100 tw-px-4 tw-py-2 tw-rounded">
                          {subItem.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={index}
                  to={item.href}
                  className={({ isActive }) =>
                    `tw-font-semibold tw-text-base tw-transition-colors tw-duration-200 ${isActive
                      ? 'tw-active-nav-link-dark tw-shine-effect'
                      : 'tw-text-global-6 hover:tw-text-blue-3 hover:tw-bg-white/10 hover:tw-backdrop-blur-md hover:tw-rounded-lg hover:tw-shadow-md tw-px-4 tw-py-2'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="tw-flex tw-items-center tw-gap-4">
            <a onClick={handleLoginClick} className="tw-text-global-6 tw-cursor-pointer tw-font-semibold tw-text-base hover:tw-text-blue-3 tw-transition-colors tw-duration-200">
              Log In
            </a>
            <button onClick={() => navigate("/sign-up")} className="tw-border-none tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] hover:tw-opacity-90 tw-text-white tw-font-bold tw-text-base tw-py-2 tw-px-4 tw-rounded-md tw-shadow-lg">
              Join Now
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`tw-bg-header-1 tw-w-full tw-absolute tw-left-0 tw-top-full tw-z-[100] tw-transition-all tw-duration-300 ${menuOpen ? 'tw-max-h-[1000px] tw-opacity-100' : 'tw-max-h-0 tw-opacity-0 tw-overflow-hidden'}`}>
        <nav className="tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-py-6">
          {menuItems.map((item, index) =>
            item.dropdown ? (
              <div key={index} className="tw-w-full tw-mb-1">
                <button onClick={() => toggleDropdown(item.label)} className="tw-w-full tw-flex tw-justify-between tw-items-center tw-text-left tw-font-semibold tw-text-base tw-text-white tw-py-2 tw-px-3 tw-rounded-md tw-bg-transparent tw-border-none focus:tw-outline-none hover:tw-bg-white/10">
                  {item.label}
                  <svg className={`tw-w-4 tw-h-4 tw-ml-1 tw-transition-transform tw-text-white ${activeDropdown === item.label ? 'tw-rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === item.label && (
                  <div className="tw-pl-6 tw-mt-1 tw-flex tw-flex-col tw-gap-1">
                    {item.dropdown.map((subItem, subIndex) => (
                      <NavLink key={subIndex} to={subItem.href} onClick={() => { setMenuOpen(false); setActiveDropdown(null); }} className="tw-text-[15px] tw-text-white hover:tw-text-blue-300 tw-font-normal tw-py-1">
                        {subItem.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink key={index} to={item.href} onClick={() => setMenuOpen(false)} className={({ isActive }) => `tw-font-semibold tw-text-base tw-transition-colors tw-duration-200 tw-text-white tw-py-2 tw-px-3 ${isActive ? 'tw-text-blue-300' : 'hover:tw-text-blue-300'}`}>
                {item.label}
              </NavLink>
            )
          )}

          <div className="tw-mt-6 tw-border-t tw-border-white/20 tw-pt-4 tw-flex tw-flex-col tw-gap-3 tw-px-3">
            <button className="tw-text-white tw-border-none tw-font-semibold tw-text-base tw-rounded-md tw-bg-blue-600 hover:tw-bg-blue-700 tw-py-2 tw-px-4" onClick={handleLoginClick}>
              Log In
            </button>
            <button onClick={() => { setMenuOpen(false); navigate("/sign-up"); }} className="tw-border-none tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] hover:tw-opacity-90 tw-text-white tw-font-bold tw-text-base tw-py-2 tw-px-4 tw-rounded-md tw-shadow-lg">
              Join Now
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
